import { useRef, useState } from 'react'
import { toast } from 'sonner'
import type { AddDictionaryWordOutcome } from '@/api/types/dictionary'
import { wordsMatch } from '@/lib/spell-check/spell-check-state'
import { notifyDictionaryChangeFailed } from '../handlers/notify-dictionary-change-failed'
import { customWordCount } from '../helpers/custom-word-count'
import { wordRuleMessage } from '../helpers/word-rule-message'

interface UseAddWordFieldOptions {
  /** The dictionary has loaded; nothing can be added before that. */
  isReady: boolean
  customWords: readonly string[]
  /** OFF makes words that differ only by capitalization the same word (ED-5). */
  caseSensitive: boolean
  onAddWord: (word: string) => Promise<AddDictionaryWordOutcome>
  /** Receives the screen-reader text for a word that was added. */
  onAnnounce: (message: string) => void
}

// Copy from the ED-5 ticket; placement from Figma "Failure Notifications / Duplicate Word".
const DUPLICATE_MESSAGE = 'This word already exists in the dictionary.'

/**
 * The Add a word field: the draft, the message under it, and the save itself. A draft that breaks a
 * word rule (Figma "Dictionary / Word Rules") shows why as it is typed and can't be added; a
 * duplicate shows its message once Add is pressed. The text stays in the field until the backend
 * has the word, and a second Add or Enter while a save runs does nothing, so repeated presses
 * can't send the word twice.
 */
export function useAddWordField({
  isReady,
  customWords,
  caseSensitive,
  onAddWord,
  onAnnounce,
}: UseAddWordFieldOptions) {
  const [draft, setDraftValue] = useState('')
  const [isDuplicate, setIsDuplicate] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  // The word to scroll to once its row mounts.
  const [addedWord, setAddedWord] = useState<string | null>(null)
  // State lags a render behind, so a second press in the same tick reads this ref instead.
  const isSavingRef = useRef(false)
  const word = draft.trim()
  const ruleMessage = wordRuleMessage(word)
  const message = ruleMessage ?? (isDuplicate ? DUPLICATE_MESSAGE : null)
  const canAdd = isReady && !isSaving && word !== '' && !ruleMessage

  const setDraft = (value: string) => {
    setDraftValue(value)
    setIsDuplicate(false)
  }

  const submit = async () => {
    if (!isReady || isSavingRef.current || !word || ruleMessage) return

    // ON: only an exact match is a duplicate. OFF: "zylostrab" duplicates "Zylostrab".
    if (customWords.some((existing) => wordsMatch(existing, word, caseSensitive))) {
      setIsDuplicate(true)

      return
    }

    isSavingRef.current = true
    setIsSaving(true)
    setAddedWord(null)

    let outcome: AddDictionaryWordOutcome

    try {
      outcome = await onAddWord(word)
    } finally {
      isSavingRef.current = false
      setIsSaving(false)
    }

    if (outcome.status === 'added') {
      const count = customWords.includes(outcome.word) ? customWords.length : customWords.length + 1

      setDraftValue('')
      setAddedWord(outcome.word)
      onAnnounce(`${outcome.word} added to dictionary. ${customWordCount(count)}.`)

      return
    }

    if (outcome.status === 'duplicate') {
      setIsDuplicate(true)

      return
    }

    // Figma "Dictionary Failure Notifications / Save Failure".
    notifyDictionaryChangeFailed(() =>
      toast.error(`Couldn't save “${word}”.`, {
        description: "The word wasn't added to the dictionary. Try again.",
      })
    )
  }

  return { draft, setDraft, message, isSaving, canAdd, submit, addedWord }
}
